import type { V2CreateAdminUserInput } from '@idc-forms/schemas/api-v2/admin-users'
import { useQueryClient } from '@tanstack/react-query'
import {
  CircleOff,
  CirclePlay,
  Info,
  KeyRound,
  Pencil,
  ShieldCheck,
  ShieldOff,
  Trash2
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { StaffStepUpDialog } from '@/features/auth/components/staff-step-up-dialog'
import useAuth from '@/features/auth/hooks/use-auth'
import { staffSessionV2Service } from '@/features/auth/staff-session-v2.service'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { FEATURES } from '@/shared/config/features'
import { queryKeys } from '@/shared/constants/query-keys'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { Checkbox } from '@/shared/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useUpdateUserV2Mutation } from '../hooks/use-user-v2-mutations'
import type { UserV2AdminRow } from '../models/user-admin-v2'
import { usersAdminV2Service } from '../services/users-admin-v2.service'
import { UserV2Form } from './user-v2-form'
import { TypeAccountBadge, userInitials } from './users-admin-columns'

type UserV2DetailPanelProps = {
  user: UserV2AdminRow
}

/**
 * Panel de detalle del usuario (v2): modo VISTA (resumen + acciones + tab "Más
 * información") y modo FORMULARIO in-place (mismo UserV2Form que la creación).
 * Todo se identifica por id público (`usr_`); las mutaciones van por la API v2.
 * Credenciales fuera del alcance: son self-service.
 */
export function UserV2DetailPanel({ user }: UserV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmPasswordReset, setConfirmPasswordReset] = useState(false)
  const [stepUpMfaPolicy, setStepUpMfaPolicy] = useState(false)
  const [stepUpMfaReset, setStepUpMfaReset] = useState(false)
  const [resetRevokeTokens, setResetRevokeTokens] = useState(false)
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateUserV2Mutation()
  const { has, isAdmin } = usePermissions()

  // Un administrador no ejecuta acciones destructivas sobre su propia cuenta desde el
  // panel: se auto-bloquearía. El equivalente vive en "Mi cuenta" (self-service). El
  // backend lo rechaza igual con SELF_ACTION_NOT_ALLOWED — esto sólo evita ofrecerlo.
  const isSelf = Boolean(auth?.publicId) && auth?.publicId === user.id

  const applyPasswordReset = async (accessToken: string) => {
    await usersAdminV2Service.resetPassword(accessToken, user.id)
    toast.success('Nueva contraseña enviada al correo del usuario')
  }

  const applyDelete = async (accessToken: string) => {
    await usersAdminV2Service.remove(accessToken, user.id)
    toast.success('Usuario eliminado')
    await queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    closePanel()
  }

  // Exigir/soltar 2FA y resetear el MFA de un usuario son operaciones sensibles: el
  // backend pide un step-up con MFA reciente (mfaMaxAgeSeconds), así que el token del
  // admin se acuña fresco dentro del StaffStepUpDialog y se usa una sola vez acá.
  const applyMfaPolicy = async (accessToken: string) => {
    const policy = await staffSessionV2Service.updateStaffMfaPolicy(accessToken, user.id, {
      required: !user.mfaRequired
    })
    toast.success(policy.required ? '2FA ahora es obligatorio.' : '2FA ahora es opcional.')
    await queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
  }

  const applyMfaReset = async (accessToken: string) => {
    await staffSessionV2Service.resetStaffMfa(accessToken, user.id, {
      revokeApiTokens: resetRevokeTokens
    })
    toast.success(
      resetRevokeTokens
        ? '2FA reseteado y API tokens revocados. El usuario deberá configurarlo de nuevo al iniciar sesión.'
        : '2FA reseteado. Sus API tokens siguen activos; deberá configurar el 2FA al iniciar sesión.'
    )
    await queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
  }

  const handleUpdate = (values: V2CreateAdminUserInput) => {
    updateMutation.mutate(
      { id: user.id, body: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: user.id, body: { active: !user.active } })
  }

  if (mode === 'edit') {
    return (
      <UserV2Form
        defaultValues={{
          fullName: user.fullName,
          username: user.username,
          email: user.email,
          accountType: user.accountType,
          roleIds: user.roles.map((role) => role.id),
          avatar: user.avatar ?? undefined,
          active: user.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex items-center gap-3">
            <Avatar seed={user.id} className="h-11 w-11">
              {user.avatar && <AvatarImage src={user.avatar} alt="" />}
              <AvatarFallback>{userInitials(user.fullName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold leading-tight">{user.fullName}</h3>
              <p className="truncate text-sm text-muted-foreground">@{user.username}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Correo" value={user.email} copyValue={user.email} />
            <DetailFieldRow label="Usuario" value={user.username} copyValue={user.username} />
            <DetailFieldRow
              label="Tipo de cuenta"
              value={<TypeAccountBadge typeAccount={user.accountType} />}
            />
            <DetailFieldRow label="Activo" value={user.active ? 'Sí' : 'No'} />
            <DetailFieldRow label="2FA" value={user.mfaEnabled ? 'Habilitado' : 'No habilitado'} />
            <DetailFieldRow
              label="2FA obligatorio"
              value={
                user.accountType === 'admin'
                  ? 'Sí, por tipo de cuenta'
                  : user.mfaRequired
                    ? 'Sí, por política administrativa'
                    : 'No'
              }
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateUsers)
              ? [
                  {
                    label: 'Editar usuario',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  ...(isSelf
                    ? []
                    : [
                        {
                          label: user.active ? 'Desactivar usuario' : 'Activar usuario',
                          icon: user.active ? (
                            <CircleOff className="h-4 w-4" />
                          ) : (
                            <CirclePlay className="h-4 w-4" />
                          ),
                          variant: 'outline' as const,
                          disabled: updateMutation.isPending,
                          onClick: toggleActive
                        }
                      ])
                ]
              : []),
            ...(isAdmin && !isSelf
              ? [
                  {
                    label: 'Renovar contraseña',
                    icon: <KeyRound className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      setConfirmPasswordReset(true)
                    }
                  }
                ]
              : []),
            // Política y reset son excluyentes: mientras el usuario NO tiene 2FA puesto se
            // gobierna la política (obligarlo o soltarlo); una vez enrolado, la única acción
            // es el rescate. Así el menú nunca muestra dos ítems de 2FA que parecen opuestos.
            ...(isAdmin &&
            !isSelf &&
            FEATURES.webAuthV2 &&
            user.accountType !== 'admin' &&
            !user.mfaEnabled
              ? [
                  {
                    label: user.mfaRequired ? 'Hacer 2FA opcional' : 'Hacer 2FA obligatorio',
                    icon: <ShieldCheck className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      setStepUpMfaPolicy(true)
                    }
                  }
                ]
              : []),
            ...(isAdmin && !isSelf && FEATURES.webAuthV2 && user.mfaEnabled
              ? [
                  {
                    label: 'Resetear 2FA',
                    icon: <ShieldOff className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      setResetRevokeTokens(false)
                      setStepUpMfaReset(true)
                    }
                  }
                ]
              : []),
            ...(has(Permission.DeleteUsers) && !isSelf
              ? [
                  {
                    label: 'Eliminar usuario',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['roles']}>
            <AccordionItem value="roles">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Roles
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-1.5 pt-2 pl-3 pr-2">
                  {user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Badge key={role.id} variant="secondary">
                        {role.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Sin roles asignados</span>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(user.createdAt)} />
                  <DetailFieldRow label="Actualizado" value={formatHumanDateTime(user.updatedAt)} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <StaffStepUpDialog
        open={confirmPasswordReset}
        onOpenChange={setConfirmPasswordReset}
        title="Renovar contraseña"
        description={`Se generará una contraseña nueva para “${user.fullName}” y se enviará a ${user.email}. La contraseña actual dejará de funcionar.`}
        confirmLabel="Renovar y enviar"
        onConfirm={applyPasswordReset}
      />

      <StaffStepUpDialog
        open={stepUpMfaPolicy}
        onOpenChange={setStepUpMfaPolicy}
        title={user.mfaRequired ? 'Hacer 2FA opcional' : 'Hacer 2FA obligatorio'}
        description={
          user.mfaRequired
            ? `“${user.fullName}” podrá mantener 2FA activo voluntariamente, pero ya no será una política obligatoria. Confirma tu identidad para aplicar el cambio.`
            : `“${user.fullName}” tendrá que activar 2FA antes de usar funciones protegidas. Confirma tu identidad para aplicar el cambio.`
        }
        confirmLabel={user.mfaRequired ? 'Hacer opcional' : 'Hacer obligatorio'}
        onConfirm={applyMfaPolicy}
      />

      <StaffStepUpDialog
        open={stepUpMfaReset}
        onOpenChange={(open) => {
          setStepUpMfaReset(open)
          if (!open) setResetRevokeTokens(false)
        }}
        title="Resetear 2FA del usuario"
        description={`Se limpiará el 2FA de “${user.fullName}” y se revocarán sus sesiones. Úsalo cuando perdió su aplicación de autenticación y sus códigos de recuperación. Confirma tu identidad para continuar.`}
        confirmLabel="Resetear 2FA"
        confirmVariant="destructive"
        onConfirm={applyMfaReset}
      >
        <div className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm">
          <Checkbox
            id="reset-revoke-tokens"
            className="mt-0.5"
            checked={resetRevokeTokens}
            onCheckedChange={(checked) => setResetRevokeTokens(checked === true)}
          />
          <label htmlFor="reset-revoke-tokens" className="cursor-pointer">
            <span className="font-medium">La cuenta fue comprometida</span>
            <span className="block font-normal text-muted-foreground">
              Revoca también sus API tokens. Sus aplicaciones conectadas dejarán de funcionar.
              Déjalo sin marcar si solo perdió su aplicación de autenticación.
            </span>
          </label>
        </div>
      </StaffStepUpDialog>

      <StaffStepUpDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminar usuario"
        description={`Se eliminará “${user.fullName}” (@${user.username}). Su usuario y correo quedarán libres para un alta nueva; el histórico que creó se conserva.`}
        confirmLabel="Eliminar"
        confirmVariant="destructive"
        onConfirm={applyDelete}
      />
    </div>
  )
}
